import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import {
  Card,
  Grid,
  Divider,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Box,
  Snackbar,
  Typography,
  InputAdornment,
  Avatar,
  Modal,
} from "@mui/material";
import Alert from '@mui/material/Alert';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { SquareX, Plus, Minus } from 'lucide-react';
import { Delete, FileCopy, Close, Edit, Save } from "@mui/icons-material";
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatDate } from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import KeyFieldStrip from 'components/v2/KeyFieldStrip';
import Tooltip from "@mui/material/Tooltip";
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import colors from "assets/theme/base/colors";
import AttachmentIcon from '@mui/icons-material/Attachment';
import CreateIcon from '@mui/icons-material/Create';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import EchoComposer from 'components/EchoComposer/EchoComposer';
import MDUserAvatar from 'components/MDUserAvatar';
import { useCurrency } from '../../context/CurrencyContext';
import axios from 'axios';
import { frontendLogger } from '../../utils/logger';
import { downloadPrivateObject } from 'utils/S3';
import AddressAutocompleteInput from 'layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput';
import { DetailField, UiDetailSection } from 'components/v2/record/DetailSection';


const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function ProductDetail() {
  const [suggestions, setSuggestions] = useState({
    locations: [],
    categories: [],
    brands: [],
    manufacturers: [],
    names: []
  });

  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('products');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { id } = useParams();
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = React.useState({
    // lead_time: "",
    quantity: "",
    location: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [quantityChange, setQuantityChange] = useState(0);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSaveModalSuccess, setIsSaveModalSuccess] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [updatedInventoryFields, setUpdatedInventoryFields] = useState({});
  const [cloneFormData, setCloneFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [originalInventory, setOriginalInventory] = useState({}); // Add this line
  const customFieldsRef = useRef(null); // Added to state declarations
  const [customFields, setCustomFields] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { currency, formatCurrency, getCurrencySymbol } = useCurrency();
  const [exchangeRate, setExchangeRate] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);

  // Echo feed state variables
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [nameCache, setNameCache] = useState({});

  const entityType = 'products'; // Since this is ProductDetail component - use plural to match backend
  const entityId = id; // Use whatever variable holds your product ID

  useEffect(() => {
    fetchAttachments();
    fetchProductData();
    fetchInventoryData();
    fetchFeedItems(); // Add Echo feed items
  }, [id]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/products`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

    // Call exchange rate function when currencies change
    useEffect(() => {
      if (product?.currency && currency) {
        fetchExchangeRate(product.currency, currency);
      }
    }, []);

  // Update formatCurrency function to use the context
  const formatProductCurrency = (value, productCurrency = null) => {
    // If no value, return empty string
    if (value === null || value === undefined || value === '') return '';
    
    // Get the product's currency or use default
    const sourceCurrency = productCurrency || product?.currency || 'INR';
    
    // If product currency matches system currency, just format normally
    if (sourceCurrency === currency) {
      return formatCurrency(value, currency);
    }
    
    // If currencies don't match, show both values
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="body2">{formatCurrency(value, sourceCurrency)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {`${formatCurrency(value * exchangeRate, currency)}`}
        </Typography>
      </Box>
    );
  };

  const fetchExchangeRate = async (sourceCurrency, targetCurrency) => {
    if (sourceCurrency === targetCurrency) {
      setExchangeRate(1);
      return;
    }
  
    try {
      const response = await axios.get(`https://open.er-api.com/v6/latest/${sourceCurrency}`);
      const rates = response.data.rates;
      const rate = rates[targetCurrency];
      setExchangeRate(rate);
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      setExchangeRate(1);
    }
  };
  

  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="products"
        objectData={product}
        isEditing={isEditing}
        onFieldUpdate={(field, value) => {
          handleFieldUpdate(field, value);
        }}
        onError={(field, error) => {
          handleFieldError(field, error); // Ensure this is called with correct field and error
        }}
        formErrors={formErrors}
      />
    );
  };

   // Modify fetchProductData to store currency information
   const fetchProductData = async () => {
    setIsLoading(true);
    try {
      const productResponse = await axiosInstance.get(`/products/${id}`);
      const productData = productResponse.data;
      
      // Set default currency if not specified
      if (!productData.currency) {
        productData.currency = 'INR';
      }
      
      // Check if product currency differs from system currency
      if (productData.currency !== currency) {
        fetchExchangeRate(productData.currency, currency);
      }

      setProduct(productData);
    } catch (error) {
      console.error("Error fetching product data:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const fetchInventoryData = async () => {
    console.log("Fetching inventory data...");
    setIsLoading(true);
    try {
      // Fetch products first
      const productsResponse = await axiosInstance.get('/products/');
      const products = productsResponse.data;
      console.log("Products:", products);

      // Fetch inventory data
      const inventoryResponse = await axiosInstance.get('/inventory/');
      const inventoryData = inventoryResponse.data;
      console.log("Inventory:", inventoryData);

      // Merge product and inventory data for the specific product
      const mergedData = products.map(product => {
        // Filter inventory records for the specific product
        const inventoryItems = inventoryData.filter(item => item.product_id === product.id);

        // Pick the latest inventory record based on `created_at`
        const inventoryItem = inventoryItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || {};

        return {
          id: inventoryItem.id || null,
          product_id: product.id,
          product_name: product.name,
          category: product.category,
          description: product.description,
          quantity: inventoryItem.quantity || 0,
          location: inventoryItem.location || '',
          updated_at: inventoryItem.updated_at || product.updated_at,
          unit_of_measurement: product.unit_of_measurement,
          tax_rate: product.tax_rate,
          discount_percentage: product.discount_percentage,
          final_unit_price: product.final_unit_price,
          created_at: inventoryItem.created_at || product.created_at,
          created_by: product.created_by,
          updated_by: product.updated_by,
        };
      });

      // Find the inventory for the specific product by ID
      const specificInventory = mergedData.find(item => item.product_id === parseInt(id, 10));

      if (specificInventory) {
        setInventory(specificInventory);
      } else {
        // Initialize empty inventory state if no match is found
        setInventory({
          id: null,
          product_id: parseInt(id, 10),
          quantity: 0,
          location: '',
        });
      }
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      setSnackbar({
        open: true,
        message: 'Error fetching inventory data',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };



  const handleEditToggle = () => {
    if (isSupportUser) {
      setSnackbar({
          open: true,
          message: 'Support users are not allowed to edit contacts.',
          severity: 'error',
      });
      return; // Exit the function early
  }
    if (isEditing) {
      handleSaveChanges();
    } else {
      setOriginalInventory(inventory); // Store original inventory values when entering edit mode
    }
    setIsEditing(!isEditing);
  };

  const handleFieldUpdate = (field, value) => {
    const inventoryFields = ['quantity', 'location'];
    let processedValue = value;

    // Handle empty values for numeric fields
    if (field === 'quantity') {
      // Convert empty string or undefined to 0, else parse as integer
      processedValue = value === '' || value === undefined ? 0 : parseInt(value, 10);
      
      // If parsing failed, set to 0
      if (isNaN(processedValue)) {
        processedValue = 0;
      }

      if (processedValue < 0) {
        setSnackbar({
          open: true,
          message: 'Quantity cannot be negative',
          severity: 'error',
        });
        return;
      }
      
      // Set to null for Service or Digital products
      if (product?.product_type === 'Service' || product?.product_type === 'Digital') {
        processedValue = null;
      }
    }

    // Handle inventory fields
    if (inventoryFields.includes(field)) {
      setInventory(prev => ({ ...prev, [field]: processedValue }));
      setUpdatedInventoryFields(prev => ({ ...prev, [field]: processedValue }));
    } else {
      // Handle other fields as before
      setProduct(prev => ({ ...prev, [field]: processedValue }));
      setUpdatedFields(prev => ({ ...prev, [field]: processedValue }));
    }
    
    // Check if we're updating product_type
    if (field === 'product_type') {
      // If changing to Service or Digital, clear and disable quantity
      if (value === 'Service' || value === 'Digital') {
        setInventory(prev => ({
          ...prev,
          quantity: 0
        }));
        
        setUpdatedInventoryFields(prev => ({
          ...prev,
          quantity: null // Set to null when sending to the database
        }));
      }
    }
    
    // Handle custom fields (fields starting with cf_)
    if (field.startsWith('cf_')) {
      processedValue = value === '' || value === undefined ? null : value;
      setProduct(prev => ({ ...prev, [field]: processedValue }));
      setUpdatedFields(prev => ({ ...prev, [field]: processedValue }));
      return;
    }

    // Handle price and tax rate fields
    if (field === 'price' || field === 'tax_rate') {
      // Convert empty values to 0 and ensure it's a valid number
      let numValue = value === '' || value === null ? 0 : parseFloat(value);

      if (isNaN(numValue)) {
        setSnackbar({
          open: true,
          message: `Please enter a valid number for ${field === 'price' ? 'unit price' : 'tax rate'}`,
          severity: 'error',
        });
        return;
      }

      // Validate tax rate range
      if (field === 'tax_rate' && (numValue > 100 || numValue < 0)) {
        setSnackbar({
          open: true,
          message: 'Tax rate must be between 0 and 100',
          severity: 'error',
        });
        return;
      }

      if (field === 'price' && numValue < 0) {
        setSnackbar({
          open: true,
          message: 'Unit price cannot be negative',
          severity: 'error',
        });
        return;
      }

      // Get current values with default fallback
      const currentPrice = field === 'price' ? numValue : parseFloat(product?.price) || 0;
      const currentTaxRate = field === 'tax_rate' ? numValue : parseFloat(product?.tax_rate) || 0;

      // Calculate Gross Price safely for display only
      const grossPrice = Number.isFinite(currentPrice)
        ? (currentPrice + (currentPrice * (currentTaxRate / 100))).toFixed(2)
        : '0.00';

      setProduct(prev => ({
        ...prev,
        [field]: numValue,
        final_unit_price: parseFloat(grossPrice), // Update for display purposes only
      }));

      setUpdatedFields(prev => ({
        ...prev,
        [field]: numValue,
        // Don't include final_unit_price in updatedFields
      }));
      return;
    }

    // Handle other numeric fields
    const numericFields = ['final_unit_price', 'lead_time'];
    if (numericFields.includes(field)) {
      processedValue = (value === '' || value === null) ? 0 : parseFloat(value);
      if (isNaN(processedValue)) {
        setSnackbar({
          open: true,
          message: `Please enter a valid number for ${field}`,
          severity: 'error',
        });
        return;
      }
    }

    // Handle inventory fields
    if (inventoryFields.includes(field)) {
      // If product type is Service or Digital, don't update quantity
      if (field === 'quantity' && (product?.product_type === 'Service' || product?.product_type === 'Digital')) {
        return; // Exit early without updating quantity
      }
      
      setInventory(prev => ({ ...prev, [field]: processedValue }));
      setUpdatedInventoryFields(prev => ({ ...prev, [field]: processedValue }));
    } else {
      // Handle regular product fields
      setProduct(prev => ({ ...prev, [field]: processedValue }));
      setUpdatedFields(prev => ({ ...prev, [field]: processedValue }));

      // Validate required name field
      if (field === 'name' && !processedValue) {
        setSnackbar({
          open: true,
          message: 'Product name cannot be empty',
          severity: 'error',
        });
      }
    }

    // Debugging logs
    console.log('Updated Fields:', {
      product: updatedFields,
      inventory: updatedInventoryFields,
    });
  };


  // Add handleFieldError function
  const handleFieldError = (field, error) => {
    setFormErrors(prev => {
      if (error) {
        console.log(`Setting error for ${field}: ${error}`);
        return { ...prev, [field]: error };
      } else {
        const { [field]: _, ...rest } = prev;
        console.log(`Clearing error for ${field}`);
        return rest;
      }
    });
  };


  const handleSaveChanges = async () => {
    console.log('Starting save, current formErrors:', formErrors);
    const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;
    await new Promise(resolve => setTimeout(resolve, 100)); // Wait for errors to propagate
    console.log('After validation, formErrors:', formErrors);
    console.log('isCustomFieldsValid:', isCustomFieldsValid);

    if (Object.keys(formErrors).length > 0 || !isCustomFieldsValid) {
      const errorMessages = Object.entries(formErrors)
        .map(([field, error]) => `${field.startsWith('cf_') ? field.replace('cf_', '') : field}: ${error}`)
        .join(', ');
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error',
      });
      return;
    }

    if (Object.keys(updatedFields).length === 0 && Object.keys(updatedInventoryFields).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const updatePromises = [];
      if (Object.keys(updatedFields).length > 0) {
        const productUpdateData = {
          ...updatedFields,
          updated_at: new Date().toISOString(),
          updated_by: user.id, // Add this line to set the updated_by field
          custom_fields: {},
        };

        // Move custom fields to a separate object
        Object.keys(productUpdateData).forEach(key => {
          if (key.startsWith('cf_')) {
            productUpdateData.custom_fields[key] = productUpdateData[key];
            delete productUpdateData[key];
          }
        });

        // Ensure name is set if not provided
        if (!productUpdateData.name && product.name) {
          productUpdateData.name = product.name;
        }

        // Handle new categories, brands, and manufacturers
        if (productUpdateData.category && !suggestions.categories.includes(productUpdateData.category)) {
          suggestions.categories.push(productUpdateData.category);
        }
        if (productUpdateData.brand && !suggestions.brands.includes(productUpdateData.brand)) {
          suggestions.brands.push(productUpdateData.brand);
        }
        if (productUpdateData.manufacturer && !suggestions.manufacturers.includes(productUpdateData.manufacturer)) {
          suggestions.manufacturers.push(productUpdateData.manufacturer);
        }

        // Update product
        updatePromises.push(axiosInstance.put(`/products/${id}`, productUpdateData));
      }
      if (Object.keys(updatedInventoryFields).length > 0 || product?.product_type === 'Service' || product?.product_type === 'Digital') {
        const inventoryResponse = await axiosInstance.get(`/inventory/details/`);
        const existingInventory = inventoryResponse.data.find(item => item.product_id === parseInt(id));
        
        // Handle inventory for different product types
        let quantityValue = null;
        
        // Only set quantity for Physical products
        if (product?.product_type === 'Physical') {
          quantityValue = updatedInventoryFields.quantity !== undefined ? 
            parseInt(updatedInventoryFields.quantity, 10) : 
            (inventory.quantity !== undefined ? parseInt(inventory.quantity, 10) : 0);
          
          // Ensure quantity is a valid integer
          if (isNaN(quantityValue)) {
            quantityValue = 0;
          }
        }
        
        const inventoryData = {
          product_id: parseInt(id),
          quantity: quantityValue,
          location: updatedInventoryFields.location ?? inventory.location ?? '',
          created_at: inventory.created_at ?? null,
          updated_at: new Date().toISOString(),
        };
        
        if (existingInventory && existingInventory.id && !isNaN(existingInventory.id)) {
          updatePromises.push(axiosInstance.put(`/inventory/${existingInventory.id}`, inventoryData));
        } else {
          updatePromises.push(axiosInstance.post(`/inventory/`, inventoryData));
        }
      }
      await Promise.all(updatePromises);
      const refreshResponse = await axiosInstance.get(`/products/${id}`);
      setProduct(refreshResponse.data);
      await fetchInventoryData();
      // Log product update
      await frontendLogger.logUpdate('Product', {
        id: id,
        updated_fields: Object.keys(updatedFields),
        changes: updatedFields,
        action: 'update',
        user: user.username,
        timestamp: new Date().toISOString(),
      });
      setSnackbar({
        open: true,
        message: 'Changes saved successfully',
        severity: 'success',
      });
      setIsEditing(false);
      setUpdatedFields({});
      setUpdatedInventoryFields({});
      setFormErrors({}); // Reset here, after successful save
    } catch (error) {
      console.error('Error saving changes:', error);
      const errorMessage = error.response?.data?.detail?.[0]?.msg ||
        error.response?.data?.detail ||
        'Error saving changes. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInventoryChange = async () => {
    try {
      await axiosInstance.post(`/inventory/adjust/${id}`, { quantity_change: quantityChange });
      fetchProductData(); // Refetch data to update inventory
      setQuantityChange(0);
    } catch (error) {
      console.error('Error adjusting inventory:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setInventory((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);


  // In handleCloneClick
  const handleCloneClick = () => {
    if (isSupportUser) {
      setSnackbar({
          open: true,
          message: 'Support users are not allowed to clone contacts.',
          severity: 'error',
      });
      return; // Exit the function early
  }
    const customFields = {};
    Object.keys(product).forEach(key => {
      if (key.startsWith('cf_')) {
        customFields[key] = product[key];
      }
    });

    const initialName = `${product.name}`; // Set initial name with " (Copy)" suffix
    setNewProductName(initialName); // Set the initial new product name

    const initialFormData = {
      name: initialName, // Use the initialName here
      sku: product.sku || '',
      hsn_sac_code: product.hsn_sac_code || '',
      category: product.category || '',
      brand: product.brand || '',
      manufacturer: product.manufacturer || '',
      description: product.description || '',
      price: product.price || 0,
      tax_rate: product.tax_rate || 0,
      quantity: inventory.quantity || 0,
      lead_time: product.lead_time || 0,
      location: inventory.location || '',
      product_type: product.product_type || 'Physical',
      currency: product.currency || 'INR', // Add currency field to clone
      ...customFields
    };

    console.log('Initial clone form data:', initialFormData);
    setCloneFormData(initialFormData);
    setIsCloneModalOpen(true);
  };

  const handleCloneFormChange = (field, value) => {
    setCloneFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'name') {
      setNewProductName(value); // Sync newProductName with the input
    }
  };


  const handleCloneProduct = async () => {
    try {
      setIsCloning(true);

      // Create the base product data
      const clonedProduct = {
        ...cloneFormData, // This now includes custom fields at the root level
        name: newProductName,
        product_status: "New",
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.username,
        updated_by_name: user.username,
        final_unit_price: parseFloat(cloneFormData.price || 0) +
          (parseFloat(cloneFormData.price || 0) * (parseFloat(cloneFormData.tax_rate || 0) / 100))
      };
      delete clonedProduct.created_at;
      delete clonedProduct.updated_at;
      delete clonedProduct.last_activity;

      console.log('Cloned product data before API call:', clonedProduct); // Debug log

      const response = await axiosInstance.post('/products/', clonedProduct);

      // Log product creation (clone)
      await frontendLogger.logCreate('Product', {
        id: response.data.id,
        name: response.data.name,
        action: 'clone',
        created_by: user.id,
        created_at: response.data.created_at || new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Product cloned successfully!',
        severity: 'success',
      });

      navigate(`/products/${response.data.id}`);
    } catch (error) {
      console.error('Error cloning product:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error cloning product. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsCloning(false);
      handleCloseCloneModal();
    }
  };


  const fetchAttachments = async () => {
    const parentTable = 'products';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId
          // skip: 0,
          // limit: 100,
          // sort_by: 'created_at',
          // sort_order: 'desc'
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      console.error('Error fetching attachments:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Error fetching attachments';

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000
      });
    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };


  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      // Log attachment deletion
      await frontendLogger.logDelete('Attachment', {
        id: attachmentId,
        parent: 'Product',
        parent_id: id,
        deleted_by: user.id,
        timestamp: new Date().toISOString(),
        user: user.username,
      });
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };


  const handleDiscardChanges = () => {
    setIsEditing(false);
    setUpdatedFields({});
    fetchProductData();
    setInventory(originalInventory);
    // fetchActivities();
    fetchAttachments();
  };

  const handleUploadSuccess = async (uploadData) => {
    console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'products',  // Assuming the parent table is 'producst', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);
      // Log attachment creation
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        parent: 'Product',
        parent_id: id,
        created_by: user.id,
        timestamp: new Date().toISOString(),
        user: user.username,
      });
      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

      // Update files or attachments state as needed
      // setFiles([...files, response.data]); // Uncomment if `files` state exists
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };

  // Enhanced delete handlers
  const handleDeleteProduct = async () => {
    try {
      await axiosInstance.delete(`/products/${id}`);
      // Log product deletion
      await frontendLogger.logDelete('Product', {
        id: id,
        deleted_by: user.id,
        timestamp: new Date().toISOString(),
        user: user.username,
      });
      navigate('/products/'); // Redirect to the inventory page after deletion
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    }
  };

  const extractUniqueSuggestions = async () => {
    try {
      const [productsResponse, inventoryResponse] = await Promise.all([
        axiosInstance.get('/products/'),
        axiosInstance.get('/inventory/details/')
      ]);

      const extractUnique = (arr, field) => {
        const uniqueValues = [...new Set(arr.map(item => item[field]).filter(value => value && value.trim() !== ''))]
          .sort((a, b) => a.localeCompare(b));

        console.log(`Unique ${field} values:`, uniqueValues);
        return uniqueValues;
      };

      const newSuggestions = {
        locations: extractUnique(inventoryResponse.data, 'location'),
        categories: extractUnique(productsResponse.data, 'category'),
        brands: extractUnique(productsResponse.data, 'brand'),
        manufacturers: extractUnique(productsResponse.data, 'manufacturer'),
        names: extractUnique(productsResponse.data, 'name')
      };
      console.log('New suggestions object:', newSuggestions);

      console.log('Full suggestions object:', newSuggestions);
      setSuggestions(newSuggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Name fetching functions for Echo feed mentions
  const fetchProductName = async (productId) => {
    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      return response.data?.name || `Product ${productId}`;
    } catch (error) {
      console.error('Error fetching product name:', error);
      return `Product ${productId}`;
    }
  };

  const fetchCaseName = async (caseId) => {
    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      return response.data?.case_number || `Case ${caseId}`;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case ${caseId}`;
    }
  };

  const fetchLeadName = async (leadId) => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      return response.data?.full_lead_name || `Lead ${leadId}`;
    } catch (error) {
      console.error('Error fetching lead name:', error);
      return `Lead ${leadId}`;
    }
  };

  const fetchAccountName = async (accountId) => {
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      return response.data?.account_name || `Account ${accountId}`;
    } catch (error) {
      console.error('Error fetching account name:', error);
      return `Account ${accountId}`;
    }
  };

  const fetchDealName = async (dealId) => {
    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      return response.data?.deal_name || `Deal ${dealId}`;
    } catch (error) {
      console.error('Error fetching deal name:', error);
      return `Deal ${dealId}`;
    }
  };

  const fetchContactName = async (contactId) => {
    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      return response.data?.full_contact_name || `Contact ${contactId}`;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      return `Contact ${contactId}`;
    }
  };

  const fetchEntityName = async (entityType, entityId) => {
    try {
      const cacheKey = `${entityType.toLowerCase()}_${entityId}`;
      if (nameCache[cacheKey]) {
        return nameCache[cacheKey];
      }

      let name;
      switch (entityType.toLowerCase()) {
        case 'leads':
          name = await fetchLeadName(entityId);
          break;
        case 'accounts':
          name = await fetchAccountName(entityId);
          break;
        case 'deals':
          name = await fetchDealName(entityId);
          break;
        case 'contacts':
          name = await fetchContactName(entityId);
          break;
        case 'orders':
          name = await fetchOrderName(entityId);
          break;
        case 'invoices':
          name = await fetchInvoiceName(entityId);
          break;
        case 'quotations':
          name = await fetchQuoteName(entityId);
          break;
        case 'products':
          name = await fetchProductName(entityId);
          break;
        case 'cases':
          name = await fetchCaseName(entityId);
          break;
        default:
          name = `${entityType} ${entityId}`;
      }

      // Cache the result
      setNameCache(prev => ({ ...prev, [cacheKey]: name }));
      return name;
    } catch (error) {
      console.error('Error fetching entity name:', error);
      return `${entityType} ${entityId}`;
    }
  };

  const fetchUserName = async (userId) => {
    try {
      const cacheKey = `user_${userId}`;
      if (nameCache[cacheKey]) {
        return nameCache[cacheKey];
      }

      const response = await axiosInstance.get(`/users/${userId}`);
      const name = response.data?.full_name || `User ${userId}`;
      
      // Cache the result
      setNameCache(prev => ({ ...prev, [cacheKey]: name }));
      return name;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return `User ${userId}`;
    }
  };

  const fetchOrderName = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      return response.data?.order_number || `Order ${orderId}`;
    } catch (error) {
      console.error('Error fetching order name:', error);
      return `Order ${orderId}`;
    }
  };

  const fetchInvoiceName = async (invoiceId) => {
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      return response.data?.invoice_number || `Invoice ${invoiceId}`;
    } catch (error) {
      console.error('Error fetching invoice name:', error);
      return `Invoice ${invoiceId}`;
    }
  };

  const fetchQuoteName = async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quotations/${quoteId}`);
      return response.data?.quotation_number || `Quote ${quoteId}`;
    } catch (error) {
      console.error('Error fetching quote name:', error);
      return `Quote ${quoteId}`;
    }
  };

  // Echo feed management functions
  const fetchFeedItems = async () => {
    try {
      setIsFeedLoading(true);
      console.log('Fetching feed items for:', { entityType, entityId });
      const response = await axiosInstance.get(`/feed-items/entity/${entityType}/${entityId}`);
      console.log('Fetched feed items:', response.data);
      console.log('Feed items count:', response.data?.length || 0);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      console.error('Error response:', error.response?.data);
      setFeedItems([]);
      // Don't show error snackbar for feed items as it's not critical
    } finally {
      setIsFeedLoading(false);
    }
  };

  const handleEchoSubmit = async (feedItem) => {
    // The feedItem is the response from the API after successful creation
    console.log('Feed item created:', feedItem);
    
    // Refresh the feed items to ensure consistency
    await fetchFeedItems();
    
    // The success message will be shown by the onSuccess callback
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);
      
      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;
      
      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }
      
      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);
      
      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });
      
      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();
        
        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });
        
        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);
      
      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));
        
        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };

  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';
    
    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }
    
    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';
    
    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];
    
    console.log('Parsing feed content:', { body, mentions });
    
    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;
    
    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });
    
    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add mention as highlighted text with immediate name display
      const [fullMatch, firstPart, secondPart] = match;
      console.log('Found mention:', { fullMatch, firstPart, secondPart }); // Debugging
      
      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;
      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }
      
      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);
        
        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }
        
        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }
      
      console.log('Found mention data:', mentionData); // Debugging
      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData} // Pass mentionData directly
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  useEffect(() => {
    extractUniqueSuggestions();
  }, []);

  useEffect(() => {
    if (product && (product.price !== undefined || product.tax_rate !== undefined)) {
      const price = parseFloat(product.price) || 0;
      const taxRate = parseFloat(product.tax_rate) || 0;

      if (Number.isFinite(price) && Number.isFinite(taxRate)) {
        const grossPrice = price + (price * (taxRate / 100));
        setProduct(prev => ({
          ...prev,
          final_unit_price: grossPrice,
        }));
      }
    }
  }, [product?.price, product?.tax_rate]);

  const truncateString = (str, length) => {
    return str?.length > length ? str.substring(0, length) + '...' : str;
  };

   // Update the renderSection function for currency fields
   const renderSection = (title, fields, renderContent = null) => {
     // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
     const sectionBody = (
       <>
      <Grid container spacing={3}>
        {fields.map((item) => {
          let displayValue = product?.[item.field] ?? inventory?.[item.field] ?? '';
          let isSpecialReadOnly = false;
          
          // Make quantity field read-only if product type is Service or Digital
          if (item.field === 'quantity' && (product?.product_type === 'Service' || product?.product_type === 'Digital')) {
            isSpecialReadOnly = true;
            displayValue = '-'; // Show dash instead of a number
          }

          // Handle default tax value
          if (item.field === 'tax_rate' || item.field === 'final_unit_price' || item.field === 'price') {
            displayValue = displayValue || 0; // Default to 0 if no value is provided for tax_rate, final_unit_price, or price
          }

          // Format dates for specific fields
          if (item.field === 'created_at' || item.field === 'updated_at') {
            // Audit dates must come from the product record, never inventory
            displayValue = formatDate(product?.[item.field], 'datetime');
          }

          // Capitalize string values except for email / audit timestamps
          if (
            typeof displayValue === 'string' &&
            item.field !== 'email' &&
            item.field !== 'created_at' &&
            item.field !== 'updated_at'
          ) {
            displayValue = capitalizeFirstLetter(displayValue);
          }

          // Add special handling for currency fields in edit mode
          const currencyFields = ['price', 'final_unit_price'];
          if (isEditing && currencyFields.includes(item.field)) {
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <TextField
                  fullWidth
                  label={capitalizeFirstLetter(item.label)}
                  value={displayValue}
                  onChange={(e) => handleFieldUpdate(item.field, e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(product?.currency || currency)}
                      </InputAdornment>
                    ),
                  }}
                  disabled={item.readOnly || false}
                  helperText={
                    product?.currency && product.currency !== currency
                      ? `Will be displayed in ${currency} (system currency)`
                      : null
                  }
                />
              </Grid>
            );
          }
          
          // Apply currency formatting for specific fields in read mode
          if (!isEditing && currencyFields.includes(item.field)) {
            return (
              <Grid item xs={12} md={6} key={item.field}>
                {isV2 ? (
                  <DetailField label={capitalizeFirstLetter(item.label)}>
                    {formatProductCurrency(displayValue, product?.currency)}
                  </DetailField>
                ) : (
                <MDBox>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    <strong>{capitalizeFirstLetter(item.label)}:</strong>{' '}
                    {formatProductCurrency(displayValue, product?.currency)}
                  </MDTypography>
                </MDBox>
                )}
              </Grid>
            );
          }

          // Add percentage symbol for specific fields in read mode
          const percentageFields = ['tax_rate'];
          if (!isEditing && percentageFields.includes(item.field)) {
            displayValue = `${displayValue}%`;
          }

          // Location — address autocomplete in edit mode
          if (item.field === 'location' && isEditing) {
            return (
                <Grid item xs={12} md={6} key={item.field}>
                  <Box
                      sx={{
                        '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
                          height: '45px', // matches the other Autocomplete fields on this page
                          alignItems: 'center',
                        },
                      }}
                  >
                    <AddressAutocompleteInput
                        label={capitalizeFirstLetter(item.label)}
                        placeholder="Enter location"
                        value={inventory?.location ?? ''}
                        onChange={(nextLocation) => handleFieldUpdate('location', nextLocation)}
                    />
                  </Box>
                </Grid>
            );
          }

          // Define suggestion fields
          const suggestionFields = ['location', 'category', 'brand', 'manufacturer', 'name'];
          const suggestionKey =
            item.field === 'category' ? 'categories' : `${item.field}s`;
          const hasSuggestions = suggestionFields.includes(item.field) && suggestions[suggestionKey]?.length > 0;

          if (isEditing && hasSuggestions && suggestions[suggestionKey]?.length) {
            return (
              <Grid item xs={12} md={6} key={item.field}>
                <Autocomplete
                  freeSolo
                  options={suggestions[suggestionKey]?.map(opt => capitalizeFirstLetter(opt)) || []}
                  value={displayValue}
                  onInputChange={(_, newValue) => {
                    handleFieldUpdate(item.field, newValue);
                  }}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleFieldUpdate(item.field, newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={capitalizeFirstLetter(item.label)}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        style: { height: 45 },
                      }}
                    />
                  )}
                />
              </Grid>
            );
          }

          return (
            <Grid item xs={12} md={6} key={item.field}>
              <InlineEditField
                label={capitalizeFirstLetter(item.label)}
                value={displayValue}
                options={item.options?.map(opt => capitalizeFirstLetter(opt)) ?? null}
                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                isEditing={isEditing}
                readOnly={item.readOnly ?? isSpecialReadOnly ?? false}
                currency={item.field === 'price' || item.field === 'final_unit_price' ? (product?.currency || currency) : null}
                renderValue={
                  (item.field === 'created_by_name' || item.field === 'updated_by_name') ?
                    (value) => (
                      <MDTypography
                        component="span"
                        onClick={() => {
                          const userId = item.field === 'created_by_name' ? product?.created_by : product?.updated_by;
                          if (userId) {
                            navigate(`/users/${userId}/profile`);
                          }
                        }}
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        sx={{
                          cursor: 'pointer'
                        }}
                      >
                        {capitalizeFirstLetter(value)}
                      </MDTypography>
                    ) :
                    (item.field === 'created_at' || item.field === 'updated_at') ?
                      (value) => (
                        <MDTypography
                          variant="body4"
                          color="text.primary"
                        >
                          {value}
                        </MDTypography>
                      ) :
                      (item.field === 'quantity' && (product?.product_type === 'Service' || product?.product_type === 'Digital')) ?
                      (value) => (
                        <Tooltip title="No stock tracking for this product type" arrow>
                          <MDTypography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{
                              fontStyle: 'italic',
                              cursor: 'help'
                            }}
                          >
                            -
                          </MDTypography>
                        </Tooltip>
                      ) :
                      undefined
                }
              />
            </Grid>
          );
        })}
      </Grid>
      {renderContent && (
        <MDBox mt={3}>
          {renderContent}
        </MDBox>
      )}
       </>
     );
     return (
       <UiDetailSection
         title={title}
         legacy={
           <MDBox
             mb={3}
             p={3}
             borderRadius="lg"
             sx={{
               backgroundColor: '#ffffff',
               boxShadow: 3,
             }}
           >
             <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
               <MDTypography variant="h6" fontWeight="medium" color="dark">{title}</MDTypography>
             </MDBox>
             {sectionBody}
           </MDBox>
         }
       >
         {sectionBody}
       </UiDetailSection>
     );
   };

  // Fetch global preferences for section order/hidden
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.product;
        if (prefs) {
          if (prefs.order) setSectionOrder(prefs.order);
          if (prefs.hidden) setHiddenSections(prefs.hidden);
        } else {
          setSectionOrder([
            'Product Information',
            'Pricing Information',
            'Inventory Information',
            'Custom Field Information',
            'Audit Information',
          ]);
          setHiddenSections([]);
        }
      } catch (err) {
        setSectionOrder([
          'Product Information',
          'Pricing Information',
          'Inventory Information',
          'Custom Field Information',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchGlobalPreferences();
    // eslint-disable-next-line
  }, []);

  const visibleSections = sectionOrder.filter(key => !hiddenSections.includes(key));

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="product"
          title="Loading Product Data"
          message="Please wait while we fetch the product information..."
        />
        
      </DashboardLayout>
    );
  }

  const handleCloseCloneModal = () => {
    setIsCloneModalOpen(false);
    setNameError('');
  };

  const renderCloneFormModal = () => (
    <Dialog
      open={isCloneModalOpen}
      onClose={() => !isCloning && setIsCloneModalOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        }
      }}
    >
      {/* Header */}
      <Box className="pearl-popup-header"
        sx={{
          backgroundColor: colors.popupHeader.background,
          px: 3,
          py: 2.5,
        }}
      >
        <MDTypography
          variant="h6"
          component="h2"
          sx={{ color: '#FFFFFF' }}
        >
          Clone Product
        </MDTypography>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, mt: 1 }}>
        <TextField
          autoFocus
          margin="dense"
          label="New Product Name"
          type="text"
          fullWidth
          value={newProductName}
          onChange={(e) => {
            setNewProductName(e.target.value);
            setNameError('');
          }}
          error={!!nameError}
          helperText={nameError}
          disabled={isCloning}
        />
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <MDButton
          onClick={() => setIsCloneModalOpen(false)}
          variant="outlined"
          color="secondary"
          disabled={isCloning}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleCloneProduct}
          variant="gradient"
          color="info"
          disabled={isCloning || !newProductName}
        >
          {isCloning ? <CircularProgress size={24} color="inherit" /> : 'Clone'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );


  const renderDeleteModal = () => (
    <Dialog
      open={isDeleteModalOpen}
      onClose={handleCloseDeleteModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        }
      }}
    >
      {/* Header */}
      <Box className="pearl-popup-header"
        sx={{
          backgroundColor: colors.popupHeader.background,
          px: 3,
          py: 2.5,
        }}
      >
        <MDTypography
          variant="h6"
          component="h2"
          sx={{ color: '#FFFFFF' }}
        >
          Delete Product
        </MDTypography>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, mt: 1 }}>
        <MDTypography variant="body1">
          Are you sure you want to delete this product? This action cannot be undone.
        </MDTypography>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <MDButton
          onClick={handleCloseDeleteModal}
          variant="outlined"
          color="secondary"
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleDeleteProduct}
          variant="gradient"
          color="error"
        >
          Delete
        </MDButton>
      </DialogActions>
    </Dialog>
  );





  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const recordActions = (
    <>
      {!isEditing && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Edit Button */}
          <Tooltip title="Edit Product" placement="top">
            <IconButton
              aria-label="edit"
              onClick={handleEditToggle}
              sx={{
                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': {
                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Edit sx={{ color: '#ffffff', fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Clone Button */}
          <Tooltip title="Clone Product" placement="top">
            <IconButton
              aria-label="clone"
              onClick={handleCloneClick}
              sx={{
                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': {
                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FileCopy sx={{ color: '#ffffff', fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Delete Button */}
          <Tooltip title="Delete Product" placement="top">
            <IconButton
              aria-label="delete"
              onClick={handleOpenDeleteModal}
              sx={{
                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': {
                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Delete sx={{ color: '#ffffff', fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Edit Mode Buttons */}
      {isEditing && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <MDButton
            variant="contained"
            onClick={handleDiscardChanges}
            disabled={isSaving}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            startIcon={<Close />}
          >
            Discard
          </MDButton>

          <MDButton
            variant="contained"
            onClick={handleSaveChanges}
            disabled={isSaving}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </MDButton>
        </Box>
      )}

      {/* Clone Dialog (if needed) */}
      <Dialog
        open={isCloneModalOpen}
        onClose={() => !isCloning && setIsCloneModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Product</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Product Name"
            type="text"
            fullWidth
            value={newProductName}
            onChange={(e) => {
              setNewProductName(e.target.value);
              setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
            disabled={isCloning}
          />
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setIsCloneModalOpen(false)}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
              },
            }}
            disabled={isCloning}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleCloneProduct}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
              },
            }}
            disabled={isCloning || !newProductName}
          >
            {isCloning ? <CircularProgress size={24} color="inherit" /> : 'Clone'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
  const recordDetailsBody = (
    <>
      {isLoading || loadingSections ? (
        <DataLoadingSpinner
          entityType="product"
          title="Loading Product Data"
          message="Please wait while we fetch the product information..."
        />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Product Information') {
              return renderSection('Product Information', [
                { label: "Name", field: "name" },
                { label: "SKU", field: "sku" },
                { label: "HSN Code", field: "hsn_sac_code" },
                { label: "Category", field: "category" },
                { label: "Brand", field: "brand" },
                { label: "Manufacturer", field: "manufacturer" },
                { label: "Product Type", field: "product_type", options: ["Physical", "Service", "Digital"] },
                { label: "Description", field: "description" },
              ]);
            }
            if (sectionLabel === 'Pricing Information') {
              return renderSection('Pricing Information', [
                { label: "Unit Price", field: "price" },
                { label: "Tax", field: "tax_rate" },
                { label: "Gross Price", field: "final_unit_price", readOnly: true },
              ]);
            }
            if (sectionLabel === 'Inventory Information') {
              // Hide inventory section for Service and Digital products
              if (product?.product_type !== 'Physical') {
                return null;
              }
              return renderSection('Inventory Information', [
                { label: "Quantity", field: "quantity" },
                { label: "Location", field: "location" },
                { label: "Lead Time (in days)", field: "lead_time" },
              ]);
            }
            if (sectionLabel === 'Custom Field Information') {
              // Hide custom fields section for Service and Digital products
              if (product?.product_type !== 'Physical') {
                return null;
              }
              return customFields && customFields.length > 0 ? renderSection('Custom Field Information', [], renderCustomFields()) : null;
            }
            if (sectionLabel === 'Audit Information') {
              return renderSection('Audit Information', [
                { label: "Created By", field: "created_by_name", readOnly: true },
                { label: "Modified By", field: "updated_by_name", readOnly: true },
                { label: "Created Date", field: "created_at", readOnly: true },
                { label: "Modified Date", field: "updated_at", readOnly: true },
              ]);
            }
            return null;
          })}
        </>
      )}
    </>
  );
  const recordSideColumn = (
    <>



      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Attachments
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        // allowedFileTypes="*/*"
        maxSizeInMB={5}
      />

      {/* Echo Feed Card */}
      <Box mt={5} />
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient" color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {/* EchoComposer removed, replaced by modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                Post a Feed
              </MDTypography>
              <EchoComposer
                onSubmit={handleEchoSubmit}
                onCancel={handleCloseEchoModal}
                placeholder="Type @ to mention users..."
                axiosInstance={axiosInstance}
                objectType="products"
                objectId={id}
                onSuccess={(message) => {
                  setSnackbar({
                    open: true,
                    message: 'Feed item created successfully',
                    severity: 'success',
                  });
                  handleCloseEchoModal();
                }}
                onError={(message) => setSnackbar({
                  open: true,
                  message: `Error creating feed item: ${message}`,
                  severity: 'error',
                })}
              />
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={3}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{ 
                maxHeight: 400, 
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      p: 2, 
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml:2  }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle2" 
                              fontWeight="medium"
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {item.created_by_name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ ml: 1 }}
                            >
                              {formatDate(item.updated_at, 'datetime')}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 1 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="products"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{ 
                                mb: 1,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: -2,
              }}>
                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  mb: 1.5 }}>
                  <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24,}} />
                </Box>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                 {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );

  const v2Main = (
    <MDBox>
      <PageHeader
        title={product?.name || ''}
        summary={[product?.sku ? `SKU ${product.sku}` : null, product?.category, product?.product_type].filter(Boolean).join(' · ')}
        actions={recordActions}
      />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2.5, mb: 2.5 }}>
            
            <KeyFieldStrip items={[
              { label: 'Price', value: formatProductCurrency(product?.final_unit_price ?? product?.price) || null },
              { label: 'Quantity', value: product?.quantity },
              { label: 'Brand', value: product?.brand },
              { label: 'Tax rate', value: product?.tax_rate != null && product?.tax_rate !== '' ? `${product.tax_rate}%` : null },
              { label: 'Created', value: product?.created_at ? formatDate(product.created_at, 'date') : null },
            ]} />
          </Card>
          {/* Each field section is its own card (DetailSection), so no wrapping Card. */}
          <MDBox>
            {recordDetailsBody}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={4}>
          {recordSideColumn}
        </Grid>
      </Grid>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography
                    variant="h6"
                    color="white"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}
                  >
                    {product?.name}
                  </MDTypography>
                  <MDBox>
                    {recordActions}
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                {recordDetailsBody}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            {recordSideColumn}
          </Grid>
        </Grid>
      </MDBox>
      )}
      {renderCloneFormModal()}
      {renderDeleteModal()}

      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="feed-delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
    </DashboardLayout>
  );
}

export default ProductDetail;